import { apiService } from '@/lib/api';
import { 
  ComprehensionQuestion, 
  ComprehensionAssessment, 
  ComprehensionResponse, 
  ComprehensionProfile,
  ApiStartAssessmentResponse,
  ApiAssessmentQuestion,
  AssessmentSession
} from '@/types/comprehension';

export class ComprehensionService {
  private static instance: ComprehensionService;
  private currentAssessmentId: string | null = null;
  
  static getInstance(): ComprehensionService {
    if (!ComprehensionService.instance) {
      ComprehensionService.instance = new ComprehensionService();
    }
    return ComprehensionService.instance;
  }

  /**
   * Get comprehension profile for a student
   */
  async getComprehensionProfile(studentId: string): Promise<ComprehensionProfile | null> {
    try {
      // Import auth service to get access token
      const { authService } = await import('./auth-service');
      const token = await authService.getValidAccessToken();
      
      if (!token) {
        console.error('No valid authentication token available');
        throw new Error('Authentication required to fetch comprehension profile');
      }

      const response = await apiService.get<ComprehensionProfile>(`/assessments`, token);
      return response;
    } catch (error) {
      console.error('Error fetching comprehension profile:', error);
      return null;
    }
  }

  /**
   * Check if student should take comprehension assessment
   */
  async shouldTakeAssessment(studentId: string): Promise<boolean> {
    try {
      const profile = await this.getComprehensionProfile(studentId);
      if (!profile) return true; // First time assessment
      
      const now = new Date();
      const nextDue = new Date(profile.next_assessment_due);
      
      return now >= nextDue || profile.should_show_assessment;
    } catch (error) {
      console.error('Error checking assessment eligibility:', error);
      return false;
    }
  }

  /**
   * Fetch comprehension assessment questions
   */
  async getAssessmentQuestions(
    classId: string, 
    subjectId?: string, 
    questionsCount?: number
  ): Promise<ComprehensionQuestion[]> {
    try {
      // Import auth service to get access token
      const { authService } = await import('./auth-service');
      const token = await authService.getValidAccessToken();
      
      if (!token) {
        console.error('No valid authentication token available');
        throw new Error('Authentication required to fetch assessment questions');
      }

      const payload = {
        classId,
        subjectId: subjectId || "",
        questionsCount: questionsCount || 20
      };

      const response = await apiService.post<ApiStartAssessmentResponse>('/assessments/start', payload, token);
      
      if (response && response.questions) {
        // Store the assessment ID for later submission
        this.currentAssessmentId = response.assessmentId;
        return this.convertApiQuestionsToComprehensionQuestions(response.questions);
      }
      
      throw new Error('No questions received from assessment API');
    } catch (error) {
      console.error('Error fetching assessment questions from API:', error);
      throw error;
    }
  }

  /**
   * Convert API response questions to frontend format
   */
  private convertApiQuestionsToComprehensionQuestions(apiQuestions: ApiAssessmentQuestion[]): ComprehensionQuestion[] {
    return apiQuestions.map(apiQuestion => ({
      id: apiQuestion.question_id,
      question: apiQuestion.question_text,
      subject: 'General', // API doesn't return subject in assessment response
      topic: apiQuestion.source_excerpt ? 'Reading Comprehension' : 'General',
      source_excerpt: apiQuestion.source_excerpt,
      options: apiQuestion.options.map((option, index) => ({
        id: option.option_id,
        text: option.text,
        explanation: '', // Remove duplicate explanation
        comprehension_level: index + 1, // Assign levels 1-4 based on option position
        score: index + 1 // Assign scores 1-4 based on option position
      }))
    }));
  }

  /**
   * Submit comprehension assessment responses
   */
  async submitAssessment(
    studentId: string, 
    responses: ComprehensionResponse[]
  ): Promise<{ success: boolean; score?: number; level?: number; error?: string }> {
    try {
      if (!this.currentAssessmentId) {
        throw new Error('No active assessment found. Please start an assessment first.');
      }

      // Import auth service to get access token
      const { authService } = await import('./auth-service');
      const token = await authService.getValidAccessToken();
      
      if (!token) {
        throw new Error('Authentication required to submit assessment');
      }

      // Prepare responses in the format expected by the API
      const apiResponses = responses.map(response => ({
        question_id: response.question_id,
        selected_option_id: response.selected_option_id,
        time_taken: response.time_taken || 45, // Default to 45 seconds if not provided
        responded_at: response.responded_at || new Date().toISOString()
      }));

      const payload = {
        responses: apiResponses
      };

      // Submit to the backend API
      const result = await apiService.post(
        `/assessments/${this.currentAssessmentId}/submit`, 
        payload, 
        token
      );

      // Clear the current assessment ID after successful submission
      this.currentAssessmentId = null;

      // Store completion status in localStorage for UI purposes
      localStorage.setItem(`comprehension_${studentId}`, JSON.stringify({
        last_assessment_date: new Date().toISOString(),
        next_assessment_due: this.getNextAssessmentDate(),
        should_show_assessment: false,
        is_due_for_reassessment: false
      }));

      return { success: true };
    } catch (error) {
      console.error('Error submitting assessment:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to submit assessment' 
      };
    }
  }

  /**
   * Get next assessment date (6 months from now)
   */
  private getNextAssessmentDate(): string {
    const date = new Date();
    date.setMonth(date.getMonth() + 6);
    return date.toISOString();
  }

  /**
   * Clear the current assessment ID (for cleanup purposes)
   */
  clearCurrentAssessment(): void {
    this.currentAssessmentId = null;
  }

  /**
   * Save assessment session to localStorage
   */
  saveAssessmentSession(session: AssessmentSession): void {
    try {
      const sessionKey = `assessment_session_${session.userId}`;
      localStorage.setItem(sessionKey, JSON.stringify(session));
    } catch (error) {
      console.error('Failed to save assessment session:', error);
    }
  }

  /**
   * Get saved assessment session from localStorage
   */
  getSavedAssessmentSession(userId: string): AssessmentSession | null {
    try {
      const sessionKey = `assessment_session_${userId}`;
      const sessionData = localStorage.getItem(sessionKey);
      if (sessionData) {
        const session = JSON.parse(sessionData);
        // Check if session is not too old (24 hours)
        const lastUpdated = new Date(session.lastUpdated);
        const now = new Date();
        const hoursDiff = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);
        
        if (hoursDiff < 24) {
          return session;
        } else {
          // Session expired, clean it up
          this.clearAssessmentSession(userId);
        }
      }
      return null;
    } catch (error) {
      console.error('Failed to get saved assessment session:', error);
      return null;
    }
  }

  /**
   * Clear saved assessment session
   */
  clearAssessmentSession(userId: string): void {
    try {
      const sessionKey = `assessment_session_${userId}`;
      localStorage.removeItem(sessionKey);
    } catch (error) {
      console.error('Failed to clear assessment session:', error);
    }
  }

  /**
   * Check if user has an active assessment session
   */
  hasActiveSession(userId: string): boolean {
    return this.getSavedAssessmentSession(userId) !== null;
  }

  /**
   * Get current assessment ID
   */
  getCurrentAssessmentId(): string | null {
    return this.currentAssessmentId;
  }

  /**
   * Set current assessment ID (used when resuming sessions)
   */
  setCurrentAssessmentId(assessmentId: string): void {
    this.currentAssessmentId = assessmentId;
  }
}

export const comprehensionService = ComprehensionService.getInstance();
